import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cn } from '@/shared/lib/cn';

export type ProgressProps = Omit<
  ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
  'children'
> & {
  /** 0–100 (or relative to `max`). */
  value: number;
  max?: number;
  indicatorClassName?: string;
};

/**
 * Determinate progress bar — Radix Progress.
 * Prefer over hand-rolled `role="progressbar"` divs.
 */
export const Progress = forwardRef<
  ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(function Progress(
  { className, value, max = 100, indicatorClassName, ...props },
  ref,
) {
  const safeMax = max > 0 ? max : 100;
  const pct = Math.min(100, Math.max(0, (value / safeMax) * 100));

  return (
    <ProgressPrimitive.Root
      ref={ref}
      value={value}
      max={safeMax}
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-bg', className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          'h-full w-full flex-1 rounded-full bg-primary transition-transform duration-300 ease-out',
          indicatorClassName,
        )}
        style={{ transform: `translateX(-${100 - pct}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
